import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { FiMail, FiArrowLeft, FiCheckCircle, FiShield, FiLock, FiSend, FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';
import showToast from '../../components/Toast';
import config from '../../config/frontend.config';
import '../auth/Auth.css';
import './ForgotPassword.css';

const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
});

const API_BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsAnimating(true);
  }, []);
  
  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/resetpassword/forgot`, { 
        EmailAddress: values.email 
      });
      
      if (response.data.statusCode === 200) {
        showToast.success(response.data.message || 'Password reset email sent successfully!');
        setSuccess(true);
      } else {
        showToast.warning(response.data.message || 'Could not send reset email.');
      }
    } catch (error) {
      console.error('Error sending reset email:', error);
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <div className="auth-background-overlay"></div>
          <div className="auth-background-content">
            <h1>Check Your Email</h1>
            <p>We've sent you a password reset link. Please check your email and follow the instructions.</p>
            <div className="auth-features">
              <div className="feature">
                <div className="feature-icon">📧</div>
                <span>Secure Delivery</span>
              </div>
              <div className="feature">
                <div className="feature-icon">🔒</div>
                <span>Bank-level Security</span>
              </div>
              <div className="feature">
                <div className="feature-icon">⚡</div>
                <span>Instant Delivery</span>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-form-container">
          <div className="auth-form-wrapper">
            <div className="success-state">
              <div className="success-icon-wrapper">
                <FiCheckCircle className="success-icon" />
              </div>
              <h2>Email Sent Successfully!</h2>
              <p>We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.</p>
              <div className="success-actions">
                <Link to="/auth" className="back-to-login-btn">
                  <FiArrowLeft />
                  Back to Login
                </Link>
                <button 
                  onClick={() => window.location.reload()} 
                  className="resend-btn"
                >
                  <FiSend />
                  Resend Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-background-overlay"></div>
        <div className="auth-background-content">
          <h1>Reset Your Password</h1>
          <p>Enter your email address and we'll send you a link to reset your password.</p>
        </div>
      </div>

      <div className="auth-form-container">
        <div className="auth-form-wrapper">
          <div className="auth-header">
            <h2>Forgot Password?</h2>
            <p>Enter your email address and we'll send you a link to reset your password</p>
          </div>

          <Formik
            initialValues={{ email: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, touched, errors }) => (
              <Form className="auth-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-wrapper">
                    <FiMail className="input-icon" />
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email address"
                      className={touched.email && errors.email ? 'error' : ''}
                    />
                  </div>
                  <ErrorMessage name="email" component="div" className="field-error" />
                </div>

                <button type="submit" className="submit-btn" disabled={isSubmitting || loading}>
                  <span>{loading ? 'Sending...' : 'Send Reset Link'}</span>
                  <FiSend />
                </button>
              </Form>
            )}
          </Formik>

          <div className="auth-footer">
            <p>
              Remember your password?
              <Link to="/auth" className="back-to-login">
                Back to Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword; 